import axios from 'axios';
import { useCallback } from 'react';
import { startPayHerePayment } from '../../Payment/Payment';
import { useCredit } from '../../../config/creditContext';

/**
 * Custom hook to encapsulate payment-related handlers for Upload.jsx
 * @param {object} params - All required state, setters, and utilities from Upload.jsx
 * @returns {object} - The payment handler functions
 */
export default function usePaymentHandlers({
  ENV,
  estimate,
  userEmail,
  setShowPayment,
  setPaymentCompleted,
  setViewState,
  setTranslationProgress,
  setTranslationStatus,
  setLoadingTranslate,
  setTranslationTask,
  setTranslatedFile,
  toast,
  filePath,
  selectedLanguage,
  startPolling,
}) {
  const { userId } = useCredit();
  // Payment success handler
  const handlePaymentSuccess = useCallback(
    async (orderId) => {
      setShowPayment(false);
      setPaymentCompleted(false);

      try {
        // Step 1: Verify payment

        const res = await axios.get(
          `${process.env.REACT_APP_DB_SERVER_URL || 'http://localhost:8000'}/payhere/status`,
          { params: { order_id: orderId } }
        );

        console.log('PAYMENT STATUS RESPONSE:', res.data);
        const { status_code, verified, message } = res.data;

        const isPaid =
          (status_code === 2 || status_code === '2') &&
          (verified === true || verified === 1 || verified === '1');

        if (!isPaid) {
          toast.error(message || 'Payment not verified. Please try again.');
          return;
        }

        // Step 2: Show UI and toast
        setPaymentCompleted(true);
        setViewState('progress');
        setTranslationProgress(5);
        setTranslationStatus('Starting');
        // toast.success(
        //   message || 'Payment verified and successful! Starting translation...'
        // );

        // Step 3: Start translation with required fields
        const doc_id = sessionStorage.getItem('doc_id');
        const translatePayload = {
          doc_path: filePath, // Must be full backend-relative path
          source_to_target: selectedLanguage,
          order_id: orderId,
          user_id: userId,
          doc_id: doc_id ? parseInt(doc_id) : null,
        };

        console.log('Sending /translate request:', translatePayload);

        const serverUrl =
          process.env.REACT_APP_ENV === 'local'
            ? process.env.REACT_APP_TRANSLATE_LOCAL_SERVER ||
              'http://localhost:6500'
            : process.env.REACT_APP_TRANSLATE_PROD_SERVER ||
              'https://www.devtranslate.paralegal.lk/api';

        const translateRes = await axios.post(
          `${serverUrl}/translate`,
          translatePayload,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        console.log('TRANSLATE RESPONSE:', translateRes.data);

        const { task_id, status } = translateRes.data;

        // Step 4: Start polling
        const isLocalMode = ENV === 'local';

        if (task_id) {
          startPolling(task_id, 'translate', status);
        } else if (isLocalMode) {
          // In local mode, we don't need task_id as we're bypassing the Celery queue
          console.log('Running in local mode, bypassing task_id check');

          // Check if the response already contains result data (direct translation in local mode)
          if (translateRes.data.result) {
            console.log(
              'Local mode translation completed immediately:',
              translateRes.data.result
            );

            // Set the same state as would happen in the polling success handler
            setTranslationTask({ ...translateRes.data, completed: true });
            setTranslatedFile({
              pdfPath: translateRes.data.result.pdf_path,
              docxPath: translateRes.data.result.docx_path,
              textPath: translateRes.data.result.text_path,
            });
            setViewState('result');
            setTranslationProgress(100);
            setTranslationStatus('Completed');
            setLoadingTranslate(false);
            // toast.success('Translation completed successfully in local mode');
          } else {
            // No result yet, just show progress
            setTranslationProgress(10);
            setTranslationStatus('Processing');
            // toast.info('Translation started in local mode');
          }
        } else {
          // toast.error('No task ID returned from translation server');
        }
      } catch (err) {
        console.error('Error verifying payment or starting translation:', err);
        toast.error('Could not verify payment status or start translation.');
      }
    },
    [
      ENV,
      filePath,
      selectedLanguage,
      setShowPayment,
      setPaymentCompleted,
      setViewState,
      setTranslationProgress,
      setTranslationStatus,
      setLoadingTranslate,
      setTranslationTask,
      setTranslatedFile,
      toast,
      startPolling,
    ]
  );

  // Payment close handler
  const handlePaymentClose = useCallback(() => {
    setShowPayment(false);
    setPaymentCompleted(false);
    toast.info('Payment cancelled or not successful. You can try again.');
  }, [setShowPayment, setPaymentCompleted, toast]);

  // Pay now handler
  const handlePayNow = useCallback(() => {
    setPaymentCompleted(false); // Always reset before starting a new payment

    startPayHerePayment({
      amount: Number(estimate?.amount_to_pay),
      wordCount: Number(estimate?.word_count),
      email: userEmail,
      onSuccess: handlePaymentSuccess,
      onError: () => {
        setShowPayment(false);
        setPaymentCompleted(false);
        toast.error('Payment failed or was declined. Please try again.');
      },
      onClose: handlePaymentClose,
    });
  }, [
    estimate,
    userEmail,
    handlePaymentSuccess,
    handlePaymentClose,
    setShowPayment,
    setPaymentCompleted,
    toast,
  ]);

  return { handlePaymentSuccess, handlePaymentClose, handlePayNow };
}
